import React, { useEffect, useState } from "react";
import {
  getJvvnlLoadSummary,
  JvvnlLoadSummaryRow,
  getJvvnlWeatherByLocation,
  getDiscomMetrics,
  DiscomMetricsResponse,
  getJvvnlDemandForecast,
} from "../../../Rest_api/RestApi";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { ChartType } from "../../../models/chart_model";
import { ChartTable } from "../../../components/table/ChartTable";
import { downloadChartTableCSV } from "../../../components/table/csvExport";
import "../../../components/styles.css";
import "./ForecastPrediction.css";
import { ChartAreaLoader } from "../../../components/Loading";
import DayMetricsCard from "./DayMetricsCard";
import MainPageContainer from "../../../components/MainPageContainer";
import Tooltip from "../../../components/tooltip";
import { SearchBox } from "../../../components/Search";
import { DynamicDatePicker } from "../../../components/DatePicker";
// import { UploadCustomSeries, UploadedSeries } from "./UploadCustomSeries";
import MetricChip from "../../../components/Metrics";
import SimilarDaysPopup from "./SimilarDaysPopup";
import { FilterTab } from "../../../components/Filters";
import MultiDateRangePicker, {
  DateRange,
} from "../../../components/MultiDateRangePicker";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import FilterMenu from "../../../components/FilterMenu";

type Option = {
  value: string | number;
  label: string;
  id?: number;
  locationId?: number;
  level?: "circle" | "transco";
};
type GroupedOption = { label: string; options: Option[] };

const ENTITY_OPTIONS: GroupedOption[] = [
  {
    label: "Circle",
    options: [
      {
        value: "circle",
        label: "KOTPUTLI",
        id: 1,
        locationId: 1,
        level: "circle",
      },
    ],
  },
  {
    label: "Substations",
    options: [
      {
        value: "BANSUR",
        label: "BANSUR",
        id: 4,
        locationId: 1,
        level: "transco",
      },
      {
        value: "BEHROR",
        label: "BEHROR",
        id: 5,
        locationId: 1,
        level: "transco",
      },
      {
        value: "KOTPUTLI",
        label: "KOTPUTLI",
        id: 3,
        locationId: 1,
        level: "transco",
      },
      {
        value: "NIMRANA",
        label: "NIMRANA",
        id: 43,
        locationId: 1,
        level: "transco",
      },
    ],
  },
];

const ALL_ENTITY_OPTIONS: Option[] = ENTITY_OPTIONS.flatMap((g) => g.options);

const WEATHER_PARAMS = [
  {
    key: "temperature_2m",
    label: "Temperature (°C)",
    chartLabel: "🌡️ Temperature (°C)",
    unit: "°C",
  },
  {
    key: "relative_humidity_2m",
    label: "Humidity (%)",
    chartLabel: "💧 Humidity (%)",
    unit: "%",
  },
  {
    key: "apparent_temperature",
    label: "Apparent Temp (°C)",
    chartLabel: "🌡️ Apparent Temp (°C)",
    unit: "°C",
  },
  {
    key: "precipitation",
    label: "Precipitation (mm)",
    chartLabel: "🌧️ Precipitation (mm)",
    unit: "mm",
  },
  { key: "rain", label: "Rain (mm)", chartLabel: "🌧️ Rain (mm)", unit: "mm" },
  {
    key: "cloud_cover",
    label: "Cloud Cover (%)",
    chartLabel: "☁️ Cloud Cover (%)",
    unit: "%",
  },
  {
    key: "wind_speed_80m",
    label: "Wind 80m (km/h)",
    chartLabel: "💨 Wind 80m (km/h)",
    unit: "km/h",
  },
  {
    key: "sunshine_duration",
    label: "Sunshine Duration (s)",
    chartLabel: "☀️ Sunshine Duration (s)",
    unit: "s",
  },
  {
    key: "direct_radiation_instant",
    label: "Direct Radiation Instant (W/m²)",
    chartLabel: "🔆 Direct Radiation Instant (W/m²)",
    unit: "W/m²",
  },
];

const WEATHER_VARIABLES: Option[] = WEATHER_PARAMS.map((w) => ({
  value: w.key,
  label: w.label,
}));

const formatYmd = (d: Date) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const ForecastPrediction = () => {
  const [loading, setLoading] = useState(false);

  const [forecastDate, setForecastDate] = useState<{
    startDate: Date;
    endDate: Date;
    key: string;
  }>({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [historicalRanges, setHistoricalRanges] = useState<DateRange[]>([]);
  const [weatherRanges, setWeatherRanges] = useState<DateRange[]>([]);
  const [weatherVariables, setWeatherVariables] = useState<Option[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<Option | null>(
    ALL_ENTITY_OPTIONS[0],
  );
  // const [uploadedSeries, setUploadedSeries] = useState<UploadedSeries[]>([]);
  const uploadedSeries = React.useMemo(() => [] as any[], []);
  const [discomMetrics, setDiscomMetrics] =
    useState<DiscomMetricsResponse | null>(null);
  const [activeView, setActiveView] = useState("Chart");
  const [showSimilarDays, setShowSimilarDays] = useState(false);
  const [similarDaysResult, setSimilarDaysResult] = useState<
    import("../../../Rest_api/RestApi").SimilarDaysResponse | null
  >(null);
  const VIEW_TABS = ["Chart", "Table"];

  const SERIES_ITEMS = ["Demand", "Generation", "Net Load"];
  const [historicalSeries, setHistoricalSeries] = useState<string[]>([]);
  const [forecastedSeries, setForecastedSeries] = useState<string[]>([
    "Demand",
    "Generation",
    "Net Load",
  ]);

  const [chartData, setChartData] = useState<
    Array<{ slot: number; slotLabel: string; [key: string]: number | string }>
  >([]);
  const [historicalLoading, setHistoricalLoading] = useState(false);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [historicalRows, setHistoricalRows] = useState<JvvnlLoadSummaryRow[]>(
    [],
  );
  const [historicalLegends, setHistoricalLegends] = useState<string[]>([]);
  const [weatherRows, setWeatherRows] = useState<
    Array<{
      date: string;
      time_block: number;
      [k: string]: number | string | null;
    }>
  >([]);
  const [weatherLegends, setWeatherLegends] = useState<
    Array<{ dataKey: string; name: string; unit: string }>
  >([]);
  const [netForecastRows, setNetForecastRows] = useState<
    Array<{
      slot: number;
      demand_mw: number;
      generation_mw: number;
      net_mw: number;
    }>
  >([]);
  // override maps: slot → edited value (MW)
  const [demandOverrides, setDemandOverrides] = useState<Map<number, number>>(
    new Map(),
  );
  const [generationOverrides, setGenerationOverrides] = useState<
    Map<number, number>
  >(new Map());
  // const [uploadLegends, setUploadLegends] = useState<Array<{ dataKey: string; name: string }>>([]);
  const uploadLegends = React.useMemo(
    () => [] as Array<{ dataKey: string; name: string }>,
    [],
  );

  useEffect(() => {
    const t1 = new Date(forecastDate.startDate);
    t1.setDate(t1.getDate() - 1);
    setHistoricalRanges([{ startDate: t1, endDate: t1, key: formatYmd(t1) }]);
  }, [formatYmd(forecastDate.startDate)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const fDate = formatYmd(forecastDate.startDate);
    const hDate = formatYmd(
      new Date(
        new Date(forecastDate.startDate).setDate(
          forecastDate.startDate.getDate() - 1,
        ),
      ),
    );
    let cancelled = false;
    setDiscomMetrics(null);
    getDiscomMetrics(hDate, fDate)
      .then((res) => {
        if (cancelled) return;
        const data = (res as any)?.data ?? res;
        setDiscomMetrics(data as DiscomMetricsResponse);
      })
      .catch(() => {
        if (!cancelled) setDiscomMetrics(null);
      });
    return () => {
      cancelled = true;
    };
  }, [formatYmd(forecastDate.startDate)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const dateStr = formatYmd(forecastDate.startDate);
    const entityLevel = selectedEntity?.level ?? "circle";
    const isCircle = entityLevel === "circle";
    const forecastParams = isCircle
      ? { level: "circle" as const, name: "KOTPUTLI", date: dateStr }
      : { level: "transco" as const, id: selectedEntity?.id, date: dateStr };
    let cancelled = false;
    setNetForecastRows([]);
    getJvvnlDemandForecast(forecastParams)
      .then(({ rows }) => {
        if (cancelled) return;
        setNetForecastRows(
          rows.map((r) => ({
            slot: r.block,
            demand_mw: r.demand_mw,
            generation_mw: r.generation_mw,
            net_mw: r.net_mw,
          })),
        );
      })
      .catch(() => {
        if (!cancelled) setNetForecastRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [
    formatYmd(forecastDate.startDate),
    selectedEntity?.id,
    selectedEntity?.level,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // reset overrides when forecast data refreshes
  useEffect(() => {
    setDemandOverrides(new Map());
    setGenerationOverrides(new Map());
  }, [netForecastRows]);

  useEffect(() => {
    if (historicalRanges.length === 0) {
      setHistoricalRows([]);
      setHistoricalLoading(false);
      return;
    }
    const entityLevel = selectedEntity?.level ?? "circle";
    const isCircle = entityLevel === "circle";
    const level = isCircle ? ("circle" as const) : ("transco" as const);
    const id = isCircle ? undefined : selectedEntity?.id;
    const name = isCircle ? "KOTPUTLI" : undefined;

    let cancelled = false;
    setHistoricalLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          historicalRanges.map((r) =>
            getJvvnlLoadSummary({
              startDate: formatYmd(r.startDate),
              endDate: formatYmd(r.endDate),
              level,
              id,
              name,
            }).catch(() => ({ rows: [] as JvvnlLoadSummaryRow[] })),
          ),
        );
        if (cancelled) return;
        setHistoricalRows(results.flatMap((r) => r.rows));
      } finally {
        if (!cancelled) setHistoricalLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.id, selectedEntity?.level, historicalRanges]);

  useEffect(() => {
    const locId = selectedEntity?.locationId;
    if (!locId || weatherRanges.length === 0 || weatherVariables.length === 0) {
      setWeatherRows([]);
      setWeatherLoading(false);
      return;
    }
    let cancelled = false;
    setWeatherLoading(true);
    (async () => {
      try {
        const results = await Promise.all(
          weatherRanges.map((r) =>
            getJvvnlWeatherByLocation({
              startDate: formatYmd(r.startDate),
              endDate: formatYmd(r.endDate),
              locationId: locId,
            }).catch(() => []),
          ),
        );
        if (cancelled) return;
        setWeatherRows(results.flat() as any);
      } finally {
        if (!cancelled) {
          setWeatherLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEntity?.locationId, weatherRanges, weatherVariables]);

  const parseMetric = (v: string | null | undefined): number | null => {
    if (v == null) return null;
    const n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  const avgOf = (nums: number[]) =>
    nums.length === 0 ? null : nums.reduce((a, b) => a + b, 0) / nums.length;

  const forecastAvgLoad = React.useMemo(() => {
    if (netForecastRows.length === 0)
      return parseMetric(discomMetrics?.forecast?.avg_load_mw);
    return avgOf(
      netForecastRows
        .map((r) => demandOverrides.get(r.slot) ?? r.demand_mw)
        .filter((v): v is number => typeof v === "number" && !isNaN(v)),
    );
  }, [netForecastRows, demandOverrides, discomMetrics]);

  const forecastAvgGen = React.useMemo(() => {
    if (netForecastRows.length === 0)
      return parseMetric(discomMetrics?.forecast?.avg_gen_mw);
    return avgOf(
      netForecastRows
        .map((r) => generationOverrides.get(r.slot) ?? r.generation_mw)
        .filter((v): v is number => typeof v === "number" && !isNaN(v)),
    );
  }, [netForecastRows, generationOverrides, discomMetrics]);

  const forecastAvgNet = React.useMemo(() => {
    if (netForecastRows.length === 0)
      return parseMetric(discomMetrics?.forecast?.avg_net_mw);
    return avgOf(
      netForecastRows
        .map((r) => {
          const dem = demandOverrides.get(r.slot) ?? r.demand_mw;
          const gen = generationOverrides.get(r.slot) ?? r.generation_mw;
          return dem - gen;
        })
        .filter((v): v is number => typeof v === "number" && !isNaN(v)),
    );
  }, [netForecastRows, demandOverrides, generationOverrides, discomMetrics]);

  useEffect(() => {
    const slotMap = new Map<
      number,
      { slot: number; slotLabel: string; [k: string]: number | string }
    >();
    const histLegendSet = new Set<string>();
    const slotFmt = (slot: number) => {
      const fmt = (mins: number) => {
        const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
        const m = String(mins % 60).padStart(2, "0");
        return `${h}:${m}`;
      };
      return `${fmt((slot - 1) * 30)}-${fmt(slot * 30)}`;
    };
    for (const r of historicalRows) {
      const demandKey = `${r.date} Demand (Historical)`;
      const genKey = `${r.date} Generation (Historical)`;
      const netKey = `${r.date} Net Load (Historical)`;
      histLegendSet.add(demandKey);
      histLegendSet.add(genKey);
      histLegendSet.add(netKey);
      const slot = r.block;
      let entry = slotMap.get(slot);
      if (!entry) {
        entry = { slot, slotLabel: slotFmt(slot) };
        slotMap.set(slot, entry);
      }
      entry[demandKey] = r.demand_mw;
      entry[genKey] = r.generation_mw;
      entry[netKey] = r.net_load_mw;
    }
    const wLegendMap = new Map<string, { name: string; unit: string }>();
    const selectedWeatherKeys = weatherVariables.map((v) => String(v.value));
    for (const w of weatherRows) {
      const slot = w.time_block;
      let entry = slotMap.get(slot);
      if (!entry) {
        const startMin = (slot - 1) * 30;
        const endMin = slot * 30;
        const fmt = (mins: number) => {
          const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
          const m = String(mins % 60).padStart(2, "0");
          return `${h}:${m}`;
        };
        entry = { slot, slotLabel: `${fmt(startMin)}-${fmt(endMin)}` };
        slotMap.set(slot, entry);
      }
      for (const k of selectedWeatherKeys) {
        const val = w[k];
        if (val === null || val === undefined) continue;
        const dataKey = `weather_${w.date}_${k}`;
        const wp = WEATHER_PARAMS.find((p) => p.key === k);
        const name = `${w.date} ${wp?.chartLabel ?? k}`;
        wLegendMap.set(dataKey, { name, unit: wp?.unit ?? "" });
        const cur = entry[dataKey];
        if (typeof val === "number") {
          if (typeof cur === "number") {
            entry[dataKey] = (cur + val) / 2;
          } else {
            entry[dataKey] = val;
          }
        }
      }
    }
    for (const r of netForecastRows) {
      let entry = slotMap.get(r.slot);
      if (!entry) {
        const startMin = (r.slot - 1) * 30;
        const endMin = r.slot * 30;
        const fmt = (mins: number) => {
          const h = String(Math.floor(mins / 60) % 24).padStart(2, "0");
          const m = String(mins % 60).padStart(2, "0");
          return `${h}:${m}`;
        };
        entry = { slot: r.slot, slotLabel: `${fmt(startMin)}-${fmt(endMin)}` };
        slotMap.set(r.slot, entry);
      }
      const demandVal = demandOverrides.get(r.slot) ?? r.demand_mw;
      const genVal = generationOverrides.get(r.slot) ?? r.generation_mw;
      entry["net_forecast_demand"] = demandVal;
      entry["net_forecast_generation"] = genVal;
      entry["net_forecast_net"] = demandVal - genVal;
    }

    // upload series block commented out — will be wired to API
    // const uLegends: Array<{ dataKey: string; name: string }> = [];
    // uploadedSeries.forEach(...)
    const merged = Array.from(slotMap.values()).sort((a, b) => a.slot - b.slot);
    setChartData(merged);
    setHistoricalLegends(Array.from(histLegendSet).sort());
    setWeatherLegends(
      Array.from(wLegendMap.entries()).map(([dataKey, v]) => ({
        dataKey,
        name: v.name,
        unit: v.unit,
      })),
    );
    // setUploadLegends(uLegends);
  }, [
    historicalRows,
    weatherRows,
    weatherVariables,
    uploadedSeries,
    netForecastRows,
    demandOverrides,
    generationOverrides,
  ]);

  const combinedLegends = React.useMemo(() => {
    const out: Array<{
      name: string;
      dataKey: string;
      type: ChartType;
      yAxisId?: string;
      unit?: string;
    }> = [];
    const histMatchesSelection = (name: string) => {
      if (historicalSeries.length === 0) return false;
      const lower = name.toLowerCase();
      return historicalSeries.some((s) => lower.includes(s.toLowerCase()));
    };
    for (const d of historicalLegends) {
      if (!histMatchesSelection(d)) continue;
      out.push({
        name: d,
        dataKey: d,
        type: ChartType.Line,
        opacity: 0.35,
      } as any);
    }
    for (const u of uploadLegends) {
      out.push({
        name: u.name,
        dataKey: u.dataKey,
        type: ChartType.Line,
        opacity: 0.35,
      } as any);
    }
    for (const w of weatherLegends) {
      out.push({
        name: w.name,
        dataKey: w.dataKey,
        type: ChartType.Line,
        yAxisId: "right",
        unit: w.unit,
        opacity: 0.35,
      } as any);
    }
    if (netForecastRows.length > 0) {
      const entityLabel = selectedEntity?.label ?? "KOTPUTLI";
      if (forecastedSeries.includes("Demand")) {
        out.push({
          name: `Circle - ${entityLabel} Demand (Forecasted)`,
          dataKey: "net_forecast_demand",
          type: ChartType.Line,
          smooth: false,
          lineWidth: 2,
          isDash: true,
          opacity: 1,
          legendColor: "#4E9AF1",
        } as any);
      }
      if (forecastedSeries.includes("Generation")) {
        out.push({
          name: `Circle - ${entityLabel} Generation (Forecasted)`,
          dataKey: "net_forecast_generation",
          type: ChartType.Line,
          smooth: false,
          lineWidth: 2,
          isDash: true,
          opacity: 1,
          legendColor: "#57C17B",
        } as any);
      }
      if (forecastedSeries.includes("Net Load")) {
        out.push({
          name: `Circle - ${entityLabel} Net (Forecasted)`,
          dataKey: "net_forecast_net",
          type: ChartType.Line,
          smooth: false,
          lineWidth: 2,
          opacity: 1,
          legendColor: "#F4A83A",
        } as any);
      }
    }
    return out;
  }, [
    historicalLegends,
    weatherLegends,
    uploadLegends,
    netForecastRows.length,
    selectedEntity?.label,
    historicalSeries,
    forecastedSeries,
  ]);

  const editableSeriesConfig = React.useMemo(() => {
    if (netForecastRows.length === 0) return undefined;
    return [
      {
        enabled: true,
        dataKey: "net_forecast_demand",
        min: 0,
        handleColor: "#4E9AF1",
        onPointChange: (payload: {
          dataIndex: number;
          newValue: number;
          row: any;
        }) => {
          const slot: number = payload.row?.slot ?? payload.dataIndex;
          setDemandOverrides((prev) =>
            new Map(prev).set(slot, payload.newValue),
          );
        },
      },
      {
        enabled: true,
        dataKey: "net_forecast_generation",
        min: 0,
        handleColor: "#57C17B",
        onPointChange: (payload: {
          dataIndex: number;
          newValue: number;
          row: any;
        }) => {
          const slot: number = payload.row?.slot ?? payload.dataIndex;
          setGenerationOverrides((prev) =>
            new Map(prev).set(slot, payload.newValue),
          );
        },
      },
    ];
  }, [netForecastRows.length]);

  const editableTableConfig = React.useMemo(() => {
    if (netForecastRows.length === 0) return undefined;
    const originalDemand: Record<string | number, number> = {};
    const originalGen: Record<string | number, number> = {};
    for (const r of netForecastRows) {
      originalDemand[r.slot] = r.demand_mw;
      originalGen[r.slot] = r.generation_mw;
    }
    return [
      {
        dataKey: "net_forecast_demand",
        rowKey: "slot",
        min: 0,
        originalByRowKey: originalDemand,
        onCellChange: ({
          rowKeyValue,
          newValue,
        }: {
          rowKeyValue: any;
          newValue: number;
          row: any;
        }) => {
          setDemandOverrides((prev) =>
            new Map(prev).set(Number(rowKeyValue), newValue),
          );
        },
        onRevert: (rowKeyValue: any) => {
          setDemandOverrides((prev) => {
            const next = new Map(prev);
            next.delete(Number(rowKeyValue));
            return next;
          });
        },
      },
      {
        dataKey: "net_forecast_generation",
        rowKey: "slot",
        min: 0,
        originalByRowKey: originalGen,
        onCellChange: ({
          rowKeyValue,
          newValue,
        }: {
          rowKeyValue: any;
          newValue: number;
          row: any;
        }) => {
          setGenerationOverrides((prev) =>
            new Map(prev).set(Number(rowKeyValue), newValue),
          );
        },
        onRevert: (rowKeyValue: any) => {
          setGenerationOverrides((prev) => {
            const next = new Map(prev);
            next.delete(Number(rowKeyValue));
            return next;
          });
        },
      },
    ];
  }, [netForecastRows]);

  return (
    <>
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Historical"
              items={SERIES_ITEMS}
              selectedItems={historicalSeries}
              setSelectedItems={setHistoricalSeries}
              showEmptySelected={false}
              sortAlphabetically={false}
            />
            <FilterMenu
              title="Forecasted"
              items={SERIES_ITEMS}
              selectedItems={forecastedSeries}
              setSelectedItems={setForecastedSeries}
              showEmptySelected={false}
              sortAlphabetically={false}
            />
          </div>
        </NewFilterContainer>
        <MainPageContainer className="!w-[calc(100vw)]">
          <div style={{ height: "calc(100vh - 100px)" }}>
            {loading ? (
              <ChartAreaLoader />
            ) : (
              <>
                {/* ── Top Bar ── */}
                <div className="fp-topbar">
                  <div className="fp-topbar-left">
                    <span className="fp-title">Load & Generation Forecast</span>
                    <div className="fp-divider-v" />
                    {/* Entity pill */}
                    <div className="fp-entity-pill">
                      <div className="fp-entity-dot" />
                      <div className="pill-select">
                        <SearchBox
                          options={ENTITY_OPTIONS as any}
                          selectedOptions={
                            selectedEntity ? [selectedEntity] : []
                          }
                          onChange={(selected) => {
                            setSelectedEntity(selected as Option | null);
                            setHistoricalRanges([]);
                            setWeatherRanges([]);
                            setWeatherVariables([]);
                            setSimilarDaysResult(null);
                          }}
                          placeholder="Select Entity"
                          isMany={false}
                          minWidth={130}
                          maxWidth={180}
                        />
                      </div>
                    </div>
                  </div>
                  <div />
                </div>

                {/* ── Filter Strip ── */}
                <div className="fp-filter-strip">
                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Date</span>
                    <DynamicDatePicker
                      defaultDates={forecastDate}
                      setDate={(dates) => {
                        const range = dates[0] ?? forecastDate;
                        setForecastDate({
                          ...range,
                          endDate: range.startDate,
                        });
                      }}
                      singleDate={true}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Historical</span>
                    <MultiDateRangePicker
                      singleDate={true}
                      selectedRanges={historicalRanges}
                      setDates={(ranges) => {
                        const normalized = ranges.map((r) => ({
                          ...r,
                          endDate: r.startDate,
                        }));
                        setHistoricalRanges(normalized);
                      }}
                      placeholder="Historical Dates"
                      minWidth={160}
                      maxWidth={240}
                      maxDate={(() => {
                        const y = new Date();
                        y.setDate(y.getDate() - 1);
                        return y.toISOString().split("T")[0];
                      })()}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Weather Dates</span>
                    <MultiDateRangePicker
                      singleDate={true}
                      selectedRanges={weatherRanges}
                      setDates={(ranges) => {
                        const normalized = ranges.map((r) => ({
                          ...r,
                          endDate: r.startDate,
                        }));
                        setWeatherRanges(normalized);
                      }}
                      placeholder="Weather Dates"
                      minWidth={160}
                      maxWidth={240}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <div className="fp-filter-item">
                    <span className="fp-filter-label">Variables</span>
                    <SearchBox
                      options={WEATHER_VARIABLES}
                      selectedOptions={weatherVariables}
                      onChange={(selected) =>
                        setWeatherVariables(
                          selected ? [selected as Option] : [],
                        )
                      }
                      placeholder="Weather Variable"
                      isMany={false}
                      minWidth={160}
                      maxWidth={240}
                    />
                  </div>

                  <div className="fp-strip-divider" />

                  <button
                    type="button"
                    className="fp-btn-similar"
                    onClick={() => setShowSimilarDays(true)}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="6" cy="6" r="4" />
                      <path d="M10 10l4 4" />
                    </svg>
                    Find Similar Days
                  </button>
                </div>
                {/* Metric cards (Actual and Forecast) */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-1.5 px-1">
                  <DayMetricsCard
                    title="Historical Day"
                    theme="blue"
                    metrics={[
                      {
                        label: "Avg. Load",
                        value: parseMetric(discomMetrics?.actual?.avg_load_mw),
                        unit: "MW",
                      },
                      {
                        label: "Avg. Gen",
                        value: parseMetric(discomMetrics?.actual?.avg_gen_mw),
                        unit: "MW",
                      },
                      {
                        label: "Avg. Net",
                        value: parseMetric(discomMetrics?.actual?.avg_net_mw),
                        unit: "MW",
                      },
                      {
                        label: "Avg. Temp",
                        value: parseMetric(discomMetrics?.actual?.avg_temp),
                        unit: "°C",
                      },
                    ]}
                  />
                  <DayMetricsCard
                    title="Forecast Day"
                    theme="orange"
                    metrics={[
                      {
                        label: "Avg. Load",
                        value: forecastAvgLoad,
                        unit: "MW",
                      },
                      { label: "Avg. Gen", value: forecastAvgGen, unit: "MW" },
                      { label: "Avg. Net", value: forecastAvgNet, unit: "MW" },
                      {
                        label: "Avg. Temp",
                        value: parseMetric(discomMetrics?.forecast?.avg_temp),
                        unit: "°C",
                      },
                    ]}
                  />
                </div>
                {/* ── Chart Section ── */}
                <div className="fp-chart-section">
                  {/* Chart/Table tab bar */}
                  <div className="fp-tab-bar">
                    <FilterTab
                      options={VIEW_TABS}
                      selected={[activeView]}
                      onClick={(option) => setActiveView(option)}
                      variant="pill-outline"
                    />
                  </div>

                  {/* Chart / Table area */}
                  <div className="fp-view-area">
                    {historicalLoading || weatherLoading ? (
                      <ChartAreaLoader />
                    ) : activeView === "Chart" ? (
                      <div className="fp-chart-placeholder">
                        <ReMixChart
                          data={chartData}
                          xDataKey="slot"
                          yAxisLabel="MW"
                          unit="MW"
                          chartContainerHeight="calc(100vh - 490px)"
                          onlyTitle={true}
                          isTimeSlot={true}
                          blockDurationMinutes={30}
                          secondYAxisLabel={
                            weatherLegends.length > 0
                              ? (WEATHER_PARAMS.find(
                                  (w) => w.key === weatherVariables[0]?.value,
                                )?.unit ?? "")
                              : undefined
                          }
                          legends={combinedLegends}
                          editableSeries={editableSeriesConfig}
                        />
                      </div>
                    ) : (
                      <div className="fp-table-placeholder">
                        <ChartTable
                          tableHeight="calc(100vh - 440px)"
                          data={chartData}
                          legends={combinedLegends}
                          unit="MW"
                          extraKeys={["slot", "slotLabel"]}
                          extraLabels={["Block", "Time"]}
                          itemsPerPage={96}
                          defaultSortKey="slot"
                          defaultSortDirection="asc"
                          filename={`JVVNL_forecast_${formatYmd(forecastDate.startDate)}`}
                          editableColumn={editableTableConfig}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Bottom Bar ── */}
                <div className="fp-bottombar">
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span className="fp-bottombar-hint">
                      Drag Demand or Generation points to edit · Click legend to
                      hide/show
                    </span>
                  </div>
                  <div className="fp-bottombar-actions">
                    <button
                      type="button"
                      className="fp-btn-reset"
                      disabled={
                        demandOverrides.size === 0 &&
                        generationOverrides.size === 0
                      }
                      onClick={() => {
                        setDemandOverrides(new Map());
                        setGenerationOverrides(new Map());
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M2 8a6 6 0 1 0 1.76-4.24" />
                        <path d="M2 2v4h4" />
                      </svg>
                      Reset
                    </button>
                    <button
                      type="button"
                      className="fp-btn-publish"
                      disabled={chartData.length === 0}
                      onClick={() => {
                        downloadChartTableCSV({
                          filename: `JVVNL_forecast_${formatYmd(forecastDate.startDate)}`,
                          legends: combinedLegends as any,
                          data: chartData,
                          unit: "MW",
                          extraKeys: ["slot", "slotLabel"],
                          extraLabels: ["Block", "Time"],
                        } as any);
                      }}
                    >
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 16 16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M8 2v9" />
                        <path d="M4 7l4 4 4-4" />
                        <path d="M2 14h12" />
                      </svg>
                      Download
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5">
              <Tooltip note={<p></p>} text="">
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>

      {showSimilarDays && (
        <SimilarDaysPopup
          targetDate={formatYmd(forecastDate.startDate)}
          locationId={selectedEntity?.locationId ?? 1}
          onClose={() => setShowSimilarDays(false)}
          persistedResult={similarDaysResult}
          onResultUpdate={setSimilarDaysResult}
          onDateSelect={(dateStr) => {
            const d = new Date(dateStr);
            setWeatherRanges((prev) => {
              const already = prev.some(
                (r) => formatYmd(r.startDate) === dateStr,
              );
              if (already) return prev;
              return [...prev, { startDate: d, endDate: d, key: dateStr }];
            });
            setHistoricalRanges((prev) => {
              const already = prev.some(
                (r) => formatYmd(r.startDate) === dateStr,
              );
              if (already) return prev;
              return [...prev, { startDate: d, endDate: d, key: dateStr }];
            });
          }}
          onDateRemove={(dateStr) => {
            setWeatherRanges((prev) =>
              prev.filter((r) => formatYmd(r.startDate) !== dateStr),
            );
            setHistoricalRanges((prev) =>
              prev.filter((r) => formatYmd(r.startDate) !== dateStr),
            );
          }}
          selectedDates={
            new Set(weatherRanges.map((r) => formatYmd(r.startDate)))
          }
          features={weatherVariables as any}
          onFeaturesChange={(f) => setWeatherVariables(f as any)}
        />
      )}
    </>
  );
};

export default ForecastPrediction;
